import React, { useEffect, useRef, useState } from 'react';
import { Plus, Play, Trash2, Tag as TagIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { EVALUATION_CATEGORIES, getCategoriesForPosition } from '../data/criteria';
import { TagsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
function parseTime(s) {
  if (!s) return 0;
  const parts = String(s).trim().split(':');
  if (parts.length === 2) return (+parts[0] || 0) * 60 + (+parts[1] || 0);
  return +parts[0] || 0;
}

let ytApiPromise = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise(resolve => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return ytApiPromise;
}

export default function VideoPlayer({ video, playerId, playerPosition, canTag = true, readOnly = false }) {
  const isYouTube = (video.source || 'youtube') === 'youtube';

  // YouTube refs
  const ytContainerRef = useRef(null);
  const ytPlayerRef = useRef(null);

  // HTML5 <video> ref
  const videoElRef = useRef(null);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ timestampSec: 0, timeInput: '0:00', label: '', rating: '', categoryId: '' });
  const { user } = useAuth();
  const { toast } = useToast();

  const categories = playerPosition ? getCategoriesForPosition(playerPosition) : EVALUATION_CATEGORIES;

  // ----- YouTube init -----
  useEffect(() => {
    if (!isYouTube) return;
    let interval;
    let cancelled = false;
    loadYT().then(YT => {
      if (cancelled || !ytContainerRef.current) return;
      ytPlayerRef.current = new YT.Player(ytContainerRef.current, {
        videoId: video.youtubeId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: { onReady: (e) => setDuration(e.target.getDuration() || 0) },
      });
      interval = setInterval(() => {
        try {
          const p = ytPlayerRef.current;
          if (p && p.getCurrentTime) {
            setCurrentTime(p.getCurrentTime());
            if (!duration && p.getDuration) setDuration(p.getDuration());
          }
        } catch (_) {}
      }, 500);
    });
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      try { ytPlayerRef.current && ytPlayerRef.current.destroy && ytPlayerRef.current.destroy(); } catch (_) {}
    };
  }, [isYouTube, video.youtubeId]); // eslint-disable-line

  // ----- HTML5 video listeners -----
  useEffect(() => {
    if (isYouTube) return;
    const el = videoElRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onLoaded = () => setDuration(el.duration || 0);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onLoaded);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [isYouTube, video.id]);

  const loadTags = () => TagsAPI.listByVideo(video.id).then(setTags).catch(() => setTags([]));
  useEffect(() => { loadTags(); }, [video.id]); // eslint-disable-line

  const getNow = () => {
    if (isYouTube) {
      try { return ytPlayerRef.current?.getCurrentTime?.() || 0; } catch (_) { return 0; }
    }
    return videoElRef.current?.currentTime || 0;
  };

  const seekTo = (sec) => {
    try {
      if (isYouTube) {
        ytPlayerRef.current?.seekTo?.(sec, true);
        ytPlayerRef.current?.playVideo?.();
      } else if (videoElRef.current) {
        videoElRef.current.currentTime = sec;
        videoElRef.current.play?.();
      }
    } catch (_) {}
  };

  const pauseNow = () => {
    try {
      if (isYouTube) ytPlayerRef.current?.pauseVideo?.();
      else videoElRef.current?.pause?.();
    } catch (_) {}
  };

  const openForm = () => {
    const sec = Math.round(getNow());
    setForm({ timestampSec: sec, timeInput: fmt(sec), label: '', rating: '', categoryId: '' });
    setShowForm(true);
    pauseNow();
  };

  const captureNow = () => {
    const sec = Math.round(getNow());
    setForm(f => ({ ...f, timestampSec: sec, timeInput: fmt(sec) }));
  };

  const saveTag = async () => {
    if (!form.label.trim()) { toast({ title: 'Add a short label' }); return; }
    try {
      const sec = parseTime(form.timeInput);
      await TagsAPI.add(video.id, {
        playerId,
        timestampSec: sec,
        label: form.label.trim(),
        rating: form.rating ? parseInt(form.rating, 10) : null,
        categoryId: form.categoryId || null,
        evaluatorName: user?.name || 'Coach',
      });
      toast({ title: 'Moment tagged', description: `${fmt(sec)}  ${form.label}` });
      setShowForm(false);
      loadTags();
    } catch (e) {
      toast({ title: 'Could not save tag' });
    }
  };

  const removeTag = async (id) => {
    await TagsAPI.remove(id);
    loadTags();
  };

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const streamUrl = !isYouTube ? `${BACKEND_URL}/api/videos/${video.id}/stream` : null;

  return (
    <div className="space-y-3">
      <div className="bg-black rounded-xl overflow-hidden">
        <div className="aspect-video">
          {isYouTube ? (
            <div ref={ytContainerRef} className="w-full h-full" />
          ) : (
            <video ref={videoElRef} src={streamUrl} controls playsInline className="w-full h-full bg-black" />
          )}
        </div>
        {/* Timeline with markers */}
        <div className="px-4 py-3 bg-white/80">
          <div className="relative h-2 bg-slate-100 rounded-full">
            <div className="absolute inset-y-0 left-0 bg-sky-500/70 rounded-full" style={{ width: `${pct}%` }} />
            {tags.map(t => {
              const p = duration > 0 ? (t.timestampSec / duration) * 100 : 0;
              return (
                <button key={t.id} onClick={() => seekTo(t.timestampSec)} title={`${fmt(t.timestampSec)}  ${t.label}`}
                  style={{ left: `${p}%` }}
                  className="absolute -top-1 -translate-x-1/2 h-4 w-4 rounded-full bg-amber-400 border-2 border-slate-900 hover:scale-125 transition-transform" />
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
            <span className="font-mono">{fmt(currentTime)} / {fmt(duration)}</span>
            {canTag && !readOnly && (
              <Button size="sm" onClick={openForm} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold h-7">
                <Plus className="h-3.5 w-3.5 mr-1" /> Tag this moment
              </Button>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-sky-500/30 rounded-xl p-4">
          <div className="grid md:grid-cols-[140px_1fr_120px_180px_auto] gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-500">Timestamp</Label>
              <div className="mt-1 flex gap-1">
                <Input value={form.timeInput} onChange={e => setForm({ ...form, timeInput: e.target.value })}
                  placeholder="0:47" className="bg-slate-50 border-slate-200 text-slate-900 font-mono" />
                <Button type="button" onClick={captureNow} title="Grab current time"
                  className="bg-slate-100 hover:bg-slate-200 text-slate-900 px-2 h-10">Now</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">What did you see?</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="Great gap control on rush"
                className="mt-1 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Rating</Label>
              <Select value={form.rating} onValueChange={v => setForm({ ...form, rating: v })}>
                <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900">
                  <SelectValue placeholder="" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Category</Label>
              <Select value={form.categoryId} onValueChange={v => setForm({ ...form, categoryId: v })}>
                <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-900">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-64">
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">Cancel</Button>
              <Button onClick={saveTag} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">Save tag</Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <TagIcon className="h-4 w-4 text-sky-600" />
          <h4 className="font-semibold text-slate-900">Coach's Moment Tags</h4>
          <span className="text-xs text-slate-500">({tags.length})</span>
        </div>
        {tags.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No moment tags yet. {canTag && !readOnly && 'Play the clip and tag key moments.'}
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {tags.map(t => {
              const cat = categories.find(c => c.id === t.categoryId);
              return (
                <li key={t.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-100 transition-colors group">
                  <button onClick={() => seekTo(t.timestampSec)}
                    className="flex items-center gap-1.5 font-mono text-sky-600 text-sm bg-sky-500/10 border border-sky-500/30 rounded px-2 py-1 hover:bg-sky-500/20">
                    <Play className="h-3 w-3" fill="currentColor" /> {fmt(t.timestampSec)}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900">{t.label}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                      <span>{t.evaluatorName}</span>
                      {cat && <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[10px]">{cat.name}</Badge>}
                      {t.rating && <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/30 font-mono text-[10px]">{t.rating}/10</Badge>}
                    </div>
                  </div>
                  {!readOnly && (
                    <button onClick={() => removeTag(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-600 transition-opacity">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
